import { JSX } from "preact";
import { useState, useCallback, useRef, useEffect } from "preact/hooks";

interface Props {
  disabled: boolean;
  onSubmit: (text: string) => void;
}

export function Composer({ disabled, onSubmit }: Props): JSX.Element {
  const [draft, setDraft] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const submit = useCallback(() => {
    const t = draft.trim();
    if (!t || disabled) return;
    onSubmit(t);
    setDraft("");
    // Keep focus in the textarea so visitors can type the next message
    // without re-tapping. preventScroll: focusing must NOT scroll the host
    // page (when embedded inline, plain focus() yanks the page to the input).
    textareaRef.current?.focus({ preventScroll: true });
  }, [draft, disabled, onSubmit]);

  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  // Keep focus in the textarea once the composer becomes enabled so the
  // typing flow is uninterrupted. preventScroll so embedding inline never
  // scrolls the host page to the input.
  useEffect(() => {
    if (!disabled) textareaRef.current?.focus({ preventScroll: true });
  }, [disabled]);

  // Auto-grow with content up to the CSS max-height (120px), then snap back to a
  // single line when it's cleared after submit — so multi-line messages aren't
  // cramped in the 44px box.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [draft]);

  return (
    <div class="acc-composer">
      <textarea
        ref={textareaRef}
        class="acc-input"
        rows={1}
        placeholder="Type your message…"
        value={draft}
        disabled={disabled}
        onInput={(e) => setDraft((e.currentTarget as HTMLTextAreaElement).value)}
        onKeyDown={onKey}
        aria-label="Message"
      />
      <button
        type="button"
        class="acc-icon-btn acc-send"
        disabled={disabled || !draft.trim()}
        onClick={submit}
        aria-label="Send message"
      >
        <ArrowUpIcon />
      </button>
    </div>
  );
}

/* ── Inline icons ───────────────────────────────────────────────────── */

function ArrowUpIcon(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 19V5M5 12l7-7 7 7"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}
